import React, { useState, useEffect } from 'react';
import { useDashboardStore } from '../store/dashboardStore';
import { VoucherValueChart } from './VoucherValueChart';
import { buildApiUrl } from '../config/api';

import { QuarterlyComparisonChart } from './QuarterlyComparisonChart';
import { PassFailChart } from './PassFailChart';

interface AnalyticsData {
    voucherValues: any;
    quarterlyBreakdown: any[];
    totalVouchers: number;
    redeemedCount: number;
    passRate: number;
    meanRedemptionTime: number;
    examPerformance?: {
        passCount: number;
        failCount: number;
        passRate: number;
        failRate: number;
        totalExams: number;
        pendingResults: number;
    };
}

export const EnhancedAnalyticsDashboard: React.FC = () => {
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState('overview');
    const [certificationFilter, setCertificationFilter] = useState('combined');
    const { filters } = useDashboardStore();

    useEffect(() => {
        fetchAnalyticsData();
    }, [certificationFilter, filters?.quarter, (filters as any)?.datasetTag]);

    useEffect(() => {
        // Listen for refresh events
        const handleRefresh = () => {
            fetchAnalyticsData();
        };

        window.addEventListener('refreshAnalytics', handleRefresh);

        return () => {
            window.removeEventListener('refreshAnalytics', handleRefresh);
        };
    }, []);

    const fetchAnalyticsData = async () => {
        try {
            setLoading(true);
            // Build query params consistently across tabs
            const params = new URLSearchParams();
            if (certificationFilter !== 'combined') {
                const sheetSource = certificationFilter === 'clf' ? 'foundational' : 'associate';
                params.set('sheetSource', sheetSource);
            }
            if (filters?.quarter) params.set('quarter', filters.quarter);
            if ((filters as any)?.datasetTag) params.set('datasetTag', (filters as any).datasetTag as string);
            params.set('ts', String(Date.now()));
            const url = buildApiUrl(`/api/analytics/comprehensive?${params.toString()}`);
            const response = await fetch(url, { method: 'GET', cache: 'no-store' });
            const result = await response.json();
            if (result.success) {
                setAnalyticsData(result.metrics);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading analytics data...</p>
                </div>
            </div>
        );
    }

    if (!analyticsData) {
        return (
            <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-4xl mb-4">📊</div>
                <div className="text-gray-600">No analytics data available</div>
                <div className="text-sm text-gray-500 mt-2">Please upload voucher data first</div>
            </div>
        );
    }

    const viewOptions = [
        { id: 'overview', label: 'Overview', icon: '📊' },
        { id: 'quarterly', label: 'Quarterly Analysis', icon: '📈' },
        { id: 'performance', label: 'Performance Metrics', icon: '🎯' },
        { id: 'trends', label: 'Trend Analysis', icon: '📉' }
    ];

    return (
        <div className="space-y-6">
            {/* Navigation */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 lg:mb-0">Enhanced Analytics Dashboard</h2>

                    {/* Certification Filter Dropdown */}
                    <div className="flex items-center space-x-3">
                        <label className="text-sm font-medium text-gray-700">Filter by:</label>
                        <select
                            value={certificationFilter}
                            onChange={(e) => setCertificationFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="combined">Combined (CLF + DVA)</option>
                            <option value="clf">CLF Only ($75 each)</option>
                            <option value="dva">DVA Only ($150 each)</option>
                        </select>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {viewOptions.map((option) => (
                        <button
                            key={option.id}
                            onClick={() => setActiveView(option.id)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeView === option.id
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            <span className="mr-2">{option.icon}</span>
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Key Metrics Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <span className="text-2xl">🎫</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Vouchers</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsData.totalVouchers}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <span className="text-2xl">✅</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Redemption Rate</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {((analyticsData.redeemedCount / analyticsData.totalVouchers) * 100).toFixed(1)}%
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <div className="p-3 bg-purple-100 rounded-lg">
                            <span className="text-2xl">🎯</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Pass Rate</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsData.passRate?.toFixed(1)}%</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <div className="p-3 bg-orange-100 rounded-lg">
                            <span className="text-2xl">💰</span>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Value</p>
                            <p className="text-2xl font-bold text-gray-900">
                                ${analyticsData.voucherValues?.totalValue?.toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dynamic Content Based on Active View */}
            {activeView === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <VoucherValueChart data={analyticsData.voucherValues} chartType="pie" />
                    <PassFailChart data={analyticsData} title="Exam Performance" />
                    <VoucherValueChart data={analyticsData.voucherValues} chartType="bar" />
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Average Redemption Time</span>
                                <span className="font-medium">{analyticsData.meanRedemptionTime} days</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">CLF Vouchers ($75 each)</span>
                                <span className="font-medium">{analyticsData.voucherValues?.foundationalCount}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">DVA Vouchers ($150 each)</span>
                                <span className="font-medium">{analyticsData.voucherValues?.associateCount}</span>
                            </div>
                            <div className="flex justify-between border-t pt-2 mt-2">
                                <span className="text-gray-600 font-medium">Total Program Value</span>
                                <span className="font-bold text-green-600">
                                    ${analyticsData.voucherValues?.totalValue?.toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeView === 'quarterly' && analyticsData.quarterlyBreakdown && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <QuarterlyComparisonChart
                        data={analyticsData.quarterlyBreakdown}
                        chartType="passRate"
                        title="Pass Rate Comparison by Quarter"
                    />
                    <QuarterlyComparisonChart
                        data={analyticsData.quarterlyBreakdown}
                        chartType="redemption"
                        title="Voucher Volume by Quarter"
                    />
                    <QuarterlyComparisonChart
                        data={analyticsData.quarterlyBreakdown}
                        chartType="volume"
                        title="Redemption Distribution"
                    />
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold mb-4">Quarterly Summary</h3>
                        <div className="space-y-4">
                            {analyticsData.quarterlyBreakdown.map((quarter: any, index: number) => (
                                <div key={index} className="border-l-4 border-blue-500 pl-4">
                                    <div className="font-medium text-gray-900">{quarter.quarter}</div>
                                    <div className="text-sm text-gray-600">
                                        {quarter.vouchersRedeemed} redeemed • {quarter.passRate}% pass rate
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {activeView === 'performance' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <h3 className="text-lg font-semibold mb-4">Overall Performance</h3>
                            <div className="text-6xl font-bold text-green-600 mb-2">
                                {analyticsData.passRate?.toFixed(1)}%
                            </div>
                            <div className="text-gray-600">Pass Rate</div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <h3 className="text-lg font-semibold mb-4">Utilization Rate</h3>
                            <div className="text-6xl font-bold text-blue-600 mb-2">
                                {((analyticsData.redeemedCount / analyticsData.totalVouchers) * 100).toFixed(1)}%
                            </div>
                            <div className="text-gray-600">Vouchers Redeemed</div>
                        </div>

                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <h3 className="text-lg font-semibold mb-4">Efficiency</h3>
                            <div className="text-6xl font-bold text-purple-600 mb-2">
                                {analyticsData.meanRedemptionTime}
                            </div>
                            <div className="text-gray-600">Avg Days to Redeem</div>
                        </div>
                    </div>

                    {/* Detailed Pass/Fail Analysis */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <PassFailChart data={analyticsData} title="Detailed Exam Performance" />
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold mb-4">Performance Insights</h3>
                            <div className="space-y-4">
                                <div className="p-4 bg-blue-50 rounded-lg">
                                    <div className="font-medium text-blue-900">Certification Breakdown</div>
                                    <div className="text-sm text-blue-700 mt-1">
                                        {certificationFilter === 'combined' ? 'Showing combined CLF + DVA results' :
                                            certificationFilter === 'clf' ? 'Showing CLF (Foundational) results only' :
                                                'Showing DVA (Associate) results only'}
                                    </div>
                                </div>

                                {analyticsData.examPerformance && (
                                    <>
                                        <div className="flex justify-between items-center py-2">
                                            <span className="text-gray-600">Exam Completion Rate</span>
                                            <span className="font-medium">
                                                {analyticsData.examPerformance.totalExams > 0 ?
                                                    (((analyticsData.examPerformance.passCount + analyticsData.examPerformance.failCount) / analyticsData.examPerformance.totalExams) * 100).toFixed(1) : 0}%
                                            </span>
                                        </div>

                                        <div className="flex justify-between items-center py-2">
                                            <span className="text-gray-600">Results Pending</span>
                                            <span className="font-medium text-yellow-600">
                                                {analyticsData.examPerformance.pendingResults}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeView === 'trends' && (
                <div className="grid grid-cols-1 gap-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold mb-4">Trend Analysis</h3>
                        <p className="text-gray-600 mb-4">
                            Comprehensive trend analysis across quarters and certification types.
                        </p>
                        {analyticsData.quarterlyBreakdown && (
                            <QuarterlyComparisonChart
                                data={analyticsData.quarterlyBreakdown}
                                chartType="passRate"
                                title="Pass Rate Trends Over Time"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};